import React, { memo, useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';

// components
import PopoverContent from './PopoverContent';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import { IconSVG, Popover } from '@ui';
import { ReactComponent as InfoIcon } from 'resources/icons/remix-icons/information-line.svg';
import { ReactComponent as AlertIcon } from 'resources/icons/remix-icons/alert-fill.svg';
import { ReactComponent as CheckIcon } from 'resources/icons/remix-icons/checkbox-circle-fill.svg';

interface IProps {
  fieldId: string;
  children: React.ReactNode | string;
  isDisabledDueToChildComments?: boolean;
}

const FieldIssue = memo(
  ({ children, fieldId, isDisabledDueToChildComments }: IProps) => {
    const { t } = useTranslation('onboarding');
    const {
      getActiveIssueByFieldId,
      openedFieldIssuePopoverId,
      setOpenedFieldIssuePopoverId,
    } = useEntityIntakeFormContext();

    const fieldIssue = useMemo(() => {
      return getActiveIssueByFieldId(fieldId);
    }, [fieldId, getActiveIssueByFieldId]);

    const handleOpenChange = (isOpen: boolean) => {
      if (isOpen) {
        setOpenedFieldIssuePopoverId(fieldId);
      } else {
        if (openedFieldIssuePopoverId && setOpenedFieldIssuePopoverId) {
          setOpenedFieldIssuePopoverId(null);
        }
      }
    };

    const safeFieldId = useMemo(() => {
      return fieldId.replace(/[^a-zA-Z0-9_-]/g, '_');
    }, [fieldId]);

    return (
      <DivAlignCenter id={`field-${safeFieldId}`}>
        {children}
        {fieldIssue ? (
          <Popover
            destroyTooltipOnHide
            open={openedFieldIssuePopoverId === fieldId}
            trigger="click"
            placement="right"
            onOpenChange={handleOpenChange}
            content={
              <PopoverContent
                isResolved={fieldIssue.isResolved}
                fieldIssue={fieldIssue.fieldIssue}
              />
            }
          >
            <Wrapper onMouseEnter={() => handleOpenChange(true)}>
              {fieldIssue.isResolved ? (
                <StyledIconSVG
                  component={CheckIcon}
                  color={colorsTheme.colorPrimary}
                />
              ) : (
                <StyledIconSVG
                  component={AlertIcon}
                  color={colorsTheme.colorWarning}
                />
              )}
            </Wrapper>
          </Popover>
        ) : null}
        {isDisabledDueToChildComments ? (
          <Popover
            content={t('intake_form.parent_field_disabled_reason')}
            placement="bottom"
          >
            <Wrapper>
              <StyledIconSVG
                component={InfoIcon}
                color={colorsTheme.colorWhite}
              />
            </Wrapper>
          </Popover>
        ) : null}
      </DivAlignCenter>
    );
  },
);

const StyledIconSVG = styled(IconSVG)`
  cursor: pointer;
  margin-left: ${({ theme }) => theme.marginXs};
`;

const Wrapper = styled(DivAlignCenter)`
  cursor: pointer;
`;

export default FieldIssue;
